/**
 * Painter registry: one painter per scene layer kind (see
 * packages/core/src/themes/scene.ts). The `PainterRegistry` type makes a
 * missing kind a compile error; test/theme-painters.test.ts double-checks at
 * run time and paints every theme through a recording context.
 */
import type { SceneLayer } from '@dropstack/core';
import { cliffsPainter } from './cliffs.ts';
import { cloudsPainter } from './clouds.ts';
import { crystalsPainter } from './crystals.ts';
import { buntingPainter, vinesPainter } from './decor.ts';
import { balloonsPainter, birdsPainter } from './flyers.ts';
import { groundPainter } from './ground.ts';
import { islandsPainter } from './islands.ts';
import { ridgesPainter } from './ridges.ts';
import { lightRaysPainter, rainbowPainter, skyPainter, sunPainter } from './sky.ts';
import { bridgePainter, structurePainter } from './structures.ts';
import { treesPainter } from './trees.ts';
import type { Extent, PaintEnv, Painter, PainterRegistry } from './types.ts';
import { volcanoPainter } from './volcano.ts';
import { icebergsPainter, reefPainter, waterfallPainter, waterPainter } from './water.ts';

export type { Extent, PaintEnv, Painter, PainterRegistry } from './types.ts';

export const PAINTERS: PainterRegistry = {
  sky: skyPainter,
  sun: sunPainter,
  rainbow: rainbowPainter,
  lightRays: lightRaysPainter,
  clouds: cloudsPainter,
  ridges: ridgesPainter,
  cliffs: cliffsPainter,
  volcano: volcanoPainter,
  islands: islandsPainter,
  water: waterPainter,
  icebergs: icebergsPainter,
  reef: reefPainter,
  waterfall: waterfallPainter,
  trees: treesPainter,
  crystals: crystalsPainter,
  structure: structurePainter,
  bridge: bridgePainter,
  balloons: balloonsPainter,
  birds: birdsPainter,
  bunting: buntingPainter,
  vines: vinesPainter,
  ground: groundPainter,
};

/**
 * The painter for a layer. The registry pairs each kind with the painter for
 * exactly that layer type, so widening it to Painter<SceneLayer> is sound —
 * TypeScript just can't correlate the kind union with the lookup by itself.
 */
export function painterFor(layer: SceneLayer): Painter<SceneLayer> {
  return PAINTERS[layer.kind] as Painter<SceneLayer>;
}

/** Whether a layer changes over time (and so must be repainted every frame while animating). */
export function isAnimated(layer: SceneLayer): boolean {
  const p = painterFor(layer);
  if (!p.moving) return false;
  return p.animated ? p.animated(layer) : true;
}

/** Vertical span a layer paints into, clamped to the viewport. */
export function layerExtent(layer: SceneLayer, env: PaintEnv): Extent {
  const { height } = env.size;
  const e = painterFor(layer).extent?.(layer, env) ?? { top: 0, bottom: height };
  const top = Math.max(0, Math.min(height, Math.floor(e.top)));
  const bottom = Math.max(top + 1, Math.min(height, Math.ceil(e.bottom)));
  return { top, bottom };
}

/** Paint the parts of a layer that never move. */
export function paintStill(ctx: CanvasRenderingContext2D, layer: SceneLayer, env: PaintEnv): void {
  const p = painterFor(layer);
  if (!p.still) return;
  ctx.save();
  p.still(ctx, layer, env);
  ctx.restore();
}

/** Paint the parts of a layer that change with `env.time`. */
export function paintMoving(ctx: CanvasRenderingContext2D, layer: SceneLayer, env: PaintEnv): void {
  const p = painterFor(layer);
  if (!p.moving) return;
  ctx.save();
  p.moving(ctx, layer, env);
  ctx.restore();
}
